/**
 * AskQuestionCard
 *
 * Codex-style clarification card that replaces the chat composer while the
 * agent's `ask_user_question` tool call is pending a user answer.
 *
 * Layout (per issue #975 + the Codex reference shot):
 *  - Numbered rows (1–4) with hotkeys.
 *  - Recommended pill when the label ends with "(Recommended)".
 *  - Optional info-icon tooltip surfacing each option's `description`.
 *  - Optional inline "Other" textarea row when `allowOther` is set.
 *  - Optional side-by-side markdown preview when any option carries
 *    `preview`.
 *  - Multi-select via checkboxes when `multiSelect` is true.
 *  - Multiple questions (1–4) render as a stacked form with one Submit.
 *  - Dismiss (Esc) returns `{ declined: true }` to the agent.
 *
 * Keyboard:
 *  - 1–4: toggle the corresponding option.
 *  - ↑ / ↓: move the active row.
 *  - Enter: submit.
 *  - Esc: dismiss.
 *
 * @module components/chat/ask-question/ask-question-card
 */

import { Button } from "@/components/ui/button"
import { Markdown } from "@/components/ui/markdown"
import { cn } from "@/lib/utils"
import type {
  AskUserAnswers,
  AskUserNotes,
  AskUserQuestion,
  AskUserQuestionOption,
  PendingAskQuestion,
} from "@/types/ask-question"
import { CornerDownLeft, Info } from "lucide-react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"

/**
 * Local per-question answer state held while the card is mounted.
 *
 * `picked` carries indices into `question.options` (single-element for
 * single-select, multi-element for `multiSelect`, the synthetic
 * `OTHER_INDEX` when the user typed into the "Other" row).
 * `otherText` is the free-form input for the "Other" row.
 */
interface QuestionAnswerDraft {
  picked: number[]
  otherText: string
}

/** Synthetic index used for the "Other" row when `allowOther` is true. */
const OTHER_INDEX = -1

export interface AskQuestionCardProps {
  pending: PendingAskQuestion
  /** Submit collected answers — chat.tsx forwards to AI SDK's `addToolResult`. */
  onSubmit: (toolCallId: string, answers: AskUserAnswers, notes?: AskUserNotes) => void
  /** User dismissed — chat.tsx replies `{ declined: true }`. */
  onDismiss: (toolCallId: string) => void
  /** Disable while the chat is mid-stream so submits don't race. */
  disabled?: boolean
  className?: string
}

/**
 * Returns true when the label ends with "(Recommended)" so we render the
 * little blue pill next to it. Case-insensitive, tolerant of extra
 * whitespace; everything else falls through untouched.
 */
function hasRecommendedSuffix(label: string): boolean {
  return /\(\s*recommended\s*\)\s*$/i.test(label.trim())
}

/** Strip the trailing "(Recommended)" so the label renders cleanly. */
function stripRecommendedSuffix(label: string): string {
  return label.replace(/\s*\(\s*recommended\s*\)\s*$/i, "").trim()
}

function PureAskQuestionCard({
  pending,
  onSubmit,
  onDismiss,
  disabled,
  className,
}: AskQuestionCardProps) {
  const questions = pending.input.questions

  /**
   * One draft per question. The shape is identical regardless of
   * `multiSelect` — single-select just keeps `picked.length <= 1`.
   * Initialised lazily and re-seeded whenever the toolCallId flips
   * (e.g. the agent fires a brand-new question after the previous one
   * was answered).
   */
  const [drafts, setDrafts] = useState<QuestionAnswerDraft[]>(() =>
    questions.map((q) => ({
      picked: defaultPickFor(q),
      otherText: "",
    })),
  )

  /**
   * Active row index for keyboard navigation. Single global cursor across
   * the stacked questions — moves through every row top-to-bottom (each
   * question contributes `options.length` (+1 if allowOther) rows).
   *
   * Seeded to the first pre-picked option, so a "(Recommended)" pre-selection
   * and the cursor land on the *same* row rather than marking two rows at
   * once. Falls back to row 0 when nothing is pre-picked.
   */
  const [activeRow, setActiveRow] = useState(() =>
    initialActiveRow(questions, questions.map((q) => defaultPickFor(q))),
  )
  const otherInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  /**
   * One option button per flat row index, so keyboard navigation can move DOM
   * focus along with the cursor.
   *
   * Without this, focus stayed parked on whichever row was clicked last while
   * ↑/↓ moved the highlight elsewhere — and the browser drew its focus ring on
   * that abandoned row, a third box on screen that looked like a third kind of
   * selection. Clicks already coincide (`toggleOption` moves the cursor to the
   * clicked row), so syncing the keyboard path is all that is missing.
   */
  const rowButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  const keyboardNavRef = useRef(false)
  useEffect(() => {
    if (!keyboardNavRef.current) return
    keyboardNavRef.current = false
    rowButtonRefs.current[activeRow]?.focus({ preventScroll: false })
  }, [activeRow])

  // Reset state when the agent issues a new question.
  useEffect(() => {
    const nextDrafts = questions.map((q) => ({
      picked: defaultPickFor(q),
      otherText: "",
    }))
    setDrafts(nextDrafts)
    setActiveRow(initialActiveRow(questions, nextDrafts.map((d) => d.picked)))
  }, [pending.toolCallId, questions])

  /**
   * Flatten the rows into a single index space so ↑/↓ and digit hotkeys
   * cross question boundaries naturally. Each entry remembers which
   * question it belongs to and which option (or `OTHER_INDEX`) it is.
   */
  const rows = useMemo(() => {
    const list: Array<{ questionIndex: number; optionIndex: number }> = []
    questions.forEach((q, qi) => {
      q.options.forEach((_, oi) => list.push({ questionIndex: qi, optionIndex: oi }))
      if (q.allowOther) {
        list.push({ questionIndex: qi, optionIndex: OTHER_INDEX })
      }
    })
    return list
  }, [questions])

  /** Flat row index each question's first option occupies. */
  const rowOffsets = useMemo(() => {
    const offsets: number[] = []
    let offset = 0
    for (const q of questions) {
      offsets.push(offset)
      offset += q.options.length + (q.allowOther ? 1 : 0)
    }
    return offsets
  }, [questions])

  /**
   * Toggle (or select) a given option in a given question's draft.
   * Single-select replaces the pick; multi-select toggles set membership;
   * "Other" focuses the inline textarea. Also drags the keyboard cursor
   * to the toggled row so the cursor follows mouse clicks (otherwise it
   * would stay parked wherever the user last keyboard-navigated to, even
   * if they clicked far away).
   */
  const toggleOption = useCallback(
    (questionIndex: number, optionIndex: number) => {
      const question = questions[questionIndex]
      const flat = rows.findIndex(
        (r) => r.questionIndex === questionIndex && r.optionIndex === optionIndex,
      )
      if (flat >= 0) setActiveRow(flat)
      setDrafts((prev) => {
        const next = [...prev]
        const draft = { ...next[questionIndex] }
        if (question.multiSelect) {
          const set = new Set(draft.picked)
          if (set.has(optionIndex)) {
            set.delete(optionIndex)
          } else {
            set.add(optionIndex)
          }
          draft.picked = [...set]
        } else {
          // Toggle off if re-clicked, otherwise replace.
          draft.picked = draft.picked[0] === optionIndex ? [] : [optionIndex]
        }
        next[questionIndex] = draft
        return next
      })

      if (optionIndex === OTHER_INDEX) {
        // Focus the textarea so the user can type right away.
        requestAnimationFrame(() => {
          otherInputRefs.current[`${pending.toolCallId}:${questionIndex}`]?.focus()
        })
      }
    },
    [questions, pending.toolCallId, rows],
  )

  /**
   * Resolves the user's draft into the wire shape the agent expects.
   * `null` means "blocking validation failed" — at least one question is
   * empty (and we'd otherwise send an ambiguous answer back).
   */
  const buildResult = useCallback((): {
    answers: AskUserAnswers
    notes: AskUserNotes
  } | null => {
    const answers: AskUserAnswers = {}
    const notes: AskUserNotes = {}
    for (let qi = 0; qi < questions.length; qi += 1) {
      const q = questions[qi]
      const draft = drafts[qi]
      const pickedLabels: string[] = []
      let otherPicked = false
      for (const optionIndex of draft.picked) {
        if (optionIndex === OTHER_INDEX) {
          otherPicked = true
        } else if (q.options[optionIndex]) {
          pickedLabels.push(q.options[optionIndex].label)
        }
      }
      if (otherPicked && draft.otherText.trim().length > 0) {
        pickedLabels.push(`Other: ${draft.otherText.trim()}`)
        notes[q.question] = draft.otherText.trim()
      }
      if (pickedLabels.length === 0) return null
      answers[q.question] = pickedLabels.join(", ")
    }
    return { answers, notes }
  }, [drafts, questions])

  const handleSubmit = useCallback(() => {
    if (disabled) return
    const result = buildResult()
    if (!result) return
    onSubmit(pending.toolCallId, result.answers, Object.keys(result.notes).length ? result.notes : undefined)
  }, [buildResult, disabled, onSubmit, pending.toolCallId])

  const handleDismiss = useCallback(() => {
    if (disabled) return
    onDismiss(pending.toolCallId)
  }, [disabled, onDismiss, pending.toolCallId])

  /**
   * Card-scoped keyboard shortcuts. Bound at the document while the card
   * is mounted; we ignore events that originated inside the "Other"
   * textarea so the user can type spaces / digits there.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isTyping =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)

      if (e.key === "Escape") {
        e.preventDefault()
        handleDismiss()
        return
      }
      if (e.key === "Enter" && !isTyping) {
        e.preventDefault()
        handleSubmit()
        return
      }
      if (isTyping) return

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        keyboardNavRef.current = true
        const delta = e.key === "ArrowDown" ? 1 : -1
        setActiveRow((r) => {
          const next = Math.max(0, Math.min(rows.length - 1, r + delta))
          // Single-select: selection follows the cursor, so arrowing onto a
          // row picks it and the one selected look tracks ↑/↓ (Codex
          // behavior) — never a second highlight. Multi-select keeps
          // selection independent of cursor — Space / click / digit
          // hotkeys toggle membership without arrow keys touching it.
          const row = rows[next]
          if (row) {
            const q = questions[row.questionIndex]
            if (!q.multiSelect) {
              setDrafts((prev) => {
                const out = [...prev]
                const d = { ...out[row.questionIndex] }
                d.picked = [row.optionIndex]
                out[row.questionIndex] = d
                return out
              })
            }
          }
          return next
        })
        return
      }
      // 1–9 hotkeys map to the first nine rows (across all stacked questions).
      const digit = Number.parseInt(e.key, 10)
      if (Number.isInteger(digit) && digit >= 1 && digit <= 9) {
        const target = rows[digit - 1]
        if (target) {
          e.preventDefault()
          keyboardNavRef.current = true
          setActiveRow(digit - 1)
          toggleOption(target.questionIndex, target.optionIndex)
        }
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [handleDismiss, handleSubmit, rows, toggleOption])

  const showAttribution = !!pending.agentName

  // Aggregate "is this submission valid" so the button can disable cleanly.
  const submitReady = useMemo(() => buildResult() !== null, [buildResult])

  /**
   * How many questions still have nothing picked.
   *
   * Submit disables until every question is answered, which is right, but with
   * two to four stacked questions a disabled button with no explanation reads
   * as a broken control — the user clicks it, nothing happens, and nothing on
   * screen says which question is still open. Counting them lets the footer say
   * so out loud.
   */
  const unansweredCount = useMemo(
    () =>
      questions.reduce((count, _question, index) => {
        const draft = drafts[index]
        const hasPick = draft.picked.some(
          (optionIndex) => optionIndex !== OTHER_INDEX || draft.otherText.trim().length > 0,
        )
        return hasPick ? count : count + 1
      }, 0),
    [drafts, questions],
  )
  const totalSelected = drafts.reduce((sum, d) => sum + d.picked.length, 0)
  const isMultiAnywhere = questions.some((q) => q.multiSelect)

  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl border border-border bg-card shadow-sm",
        // Bound height so tall multi-question cards keep the footer in
        // view; the question list scrolls internally instead of pushing
        // Submit below the viewport.
        "max-h-[75vh]",
        className,
      )}
      role="dialog"
      aria-label="Question for you"
    >
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {showAttribution ? (
          <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-foreground">
              Asked by
            </span>
            <span className="truncate font-medium text-foreground">
              {pending.agentName}
            </span>
          </div>
        ) : null}

        <div className="space-y-6">
          {questions.map((q, qi) => (
            <div
              key={`${pending.toolCallId}:${qi}`}
              // Subtle separator + extra top padding when stacking 2+
              // questions, so each block reads as its own section instead
              // of one wall of options.
              className={cn(qi > 0 && "border-t border-border pt-6")}
            >
              <QuestionBlock
                question={q}
                draft={drafts[qi]}
                activeRowIndex={rowIndexInQuestion(rows, activeRow, qi)}
                onSelect={(oi) => toggleOption(qi, oi)}
                onOtherChange={(text) =>
                  setDrafts((prev) => {
                    const next = [...prev]
                    next[qi] = { ...next[qi], otherText: text }
                    return next
                  })
                }
                otherInputRef={(el) => {
                  otherInputRefs.current[`${pending.toolCallId}:${qi}`] = el
                }}
                registerRowRef={(optionIndex, el) => {
                  rowButtonRefs.current[rowOffsets[qi] + optionIndex] = el
                }}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-border bg-card px-5 py-3">
        {/*
          Escape hatch — always visible regardless of the agent's
          `allowOther` setting. Sends `declined: true` to the tool call so
          the agent stops waiting on the card, AND restores the chat
          composer so the user can immediately type a free-form answer.
          Labeled per the wireframe so the action is unambiguous.
        */}
        <button
          type="button"
          onClick={handleDismiss}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground",
            disabled && "pointer-events-none opacity-50",
          )}
          title="Send a 'declined' result to the agent and bring back the normal composer so you can type a custom answer."
        >
          Cancel &amp; type instead
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase">
            esc
          </kbd>
        </button>
        {/*
          Say why Submit is disabled instead of leaving a dead button. Only
          when more than one question is on screen — with a single question the
          disabled state is self-explanatory and the hint would be noise.
        */}
        {!submitReady && questions.length > 1 && unansweredCount > 0 && (
          <span className="text-xs text-muted-foreground">
            {unansweredCount === 1
              ? "1 question left to answer"
              : `${unansweredCount} questions left to answer`}
          </span>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || !submitReady}
          size="sm"
          className="gap-1.5"
        >
          {isMultiAnywhere ? `Submit (${totalSelected} selected)` : "Submit"}
          <CornerDownLeft className="h-3 w-3 opacity-80" />
        </Button>
      </div>
    </div>
  )
}

export const AskQuestionCard = memo(PureAskQuestionCard)
AskQuestionCard.displayName = "AskQuestionCard"

/* ───────────────────────── helpers + sub-component ───────────────────────── */

/**
 * Picks the first "(Recommended)" option as the initial selection for
 * single-select questions. Multi-select starts empty and the user opts in.
 */
function defaultPickFor(q: AskUserQuestion): number[] {
  if (q.multiSelect) return []
  const recommended = q.options.findIndex((opt) => hasRecommendedSuffix(opt.label))
  return recommended >= 0 ? [recommended] : []
}

/**
 * Picks the initial cursor row so it lines up with whatever's already
 * selected. Walks the questions in order, taking the first pre-picked
 * option index it finds, and maps that back into the flat row space.
 * Returns 0 when nothing is pre-picked (typical for multi-select-only forms).
 */
function initialActiveRow(
  questions: AskUserQuestion[],
  picks: number[][],
): number {
  let offset = 0
  for (let qi = 0; qi < questions.length; qi += 1) {
    const q = questions[qi]
    const rowsInThisQuestion = q.options.length + (q.allowOther ? 1 : 0)
    const firstPicked = picks[qi]?.[0]
    if (typeof firstPicked === "number" && firstPicked >= 0) {
      return offset + firstPicked
    }
    offset += rowsInThisQuestion
  }
  return 0
}

/**
 * Maps the global active row to the local row index inside one question
 * (or returns -1 when the active row belongs to a different question).
 */
function rowIndexInQuestion(
  rows: Array<{ questionIndex: number; optionIndex: number }>,
  activeRow: number,
  qi: number,
): number {
  const row = rows[activeRow]
  if (!row || row.questionIndex !== qi) return -1
  return row.optionIndex
}

interface QuestionBlockProps {
  question: AskUserQuestion
  draft: QuestionAnswerDraft
  activeRowIndex: number
  onSelect: (optionIndex: number) => void
  onOtherChange: (text: string) => void
  otherInputRef: (el: HTMLInputElement | null) => void
  /** Reports each option button to the card, keyed by local option index. */
  registerRowRef: (optionIndex: number, el: HTMLButtonElement | null) => void
}

function QuestionBlock({
  question,
  draft,
  activeRowIndex,
  onSelect,
  onOtherChange,
  otherInputRef,
  registerRowRef,
}: QuestionBlockProps) {
  /**
   * Only mount the side-by-side preview pane for previews that actually
   * earn the screen-real-estate — multi-line or visually structural
   * content (mockups, ASCII diagrams, code blocks, tables). Trivial
   * single-line strings (most often a model echoing the label or
   * description into `preview` by accident) get hidden so the card stays
   * compact and the right pane doesn't fill with noise like "#social".
   */
  const hasMeaningfulPreview = question.options.some((opt) => {
    const text = opt.preview?.trim()
    if (!text) return false
    return text.includes("\n") || text.length >= 80
  })
  const hasPreview = hasMeaningfulPreview
  /**
   * Preview follows the keyboard cursor, not the picked option. That's the
   * only definition that makes sense for multi-select with previews —
   * "what does option N look like?" — you can browse with ↑/↓ even after
   * you've ticked several boxes. For single-select the cursor and the
   * pick stay in sync (see toggleOption), so the two are equivalent.
   * Falls back to the first picked option when the cursor is parked in
   * another question, then to the first option for the initial render.
   */
  const previewIndex =
    activeRowIndex >= 0
      ? activeRowIndex
      : draft.picked.find((i) => i >= 0) ?? 0
  const previewSource = question.options[previewIndex]?.preview

  return (
    <div>
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="text-[15px] font-medium leading-snug text-foreground">
          {question.question}
        </p>
        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
          {question.header}
        </span>
      </div>

      <div
        className={cn(
          "grid gap-4",
          hasPreview ? "grid-cols-1 sm:grid-cols-[minmax(0,260px)_1fr]" : "grid-cols-1",
        )}
      >
        <ul className="-mx-1 space-y-1.5">
          {question.options.map((opt, oi) => (
            <OptionRow
              key={oi}
              option={opt}
              questionNumber={oi + 1}
              selected={draft.picked.includes(oi)}
              active={activeRowIndex === oi}
              multiSelect={!!question.multiSelect}
              onClick={() => onSelect(oi)}
              rowRef={(el) => registerRowRef(oi, el)}
            />
          ))}
          {question.allowOther ? (
            <OtherRow
              questionNumber={question.options.length + 1}
              selected={draft.picked.includes(OTHER_INDEX)}
              active={activeRowIndex === OTHER_INDEX}
              value={draft.otherText}
              onSelect={() => onSelect(OTHER_INDEX)}
              onChange={onOtherChange}
              inputRef={otherInputRef}
            />
          ) : null}
        </ul>

        {hasPreview ? (
          <div className="hidden border-l border-border pl-4 sm:block">
            <p className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              Preview
            </p>
            {previewSource ? (
              // Use the chat Markdown renderer so fenced code blocks
              // (```text / ```ascii / ```sql etc.) get the same styled
              // treatment as the rest of the chat — language tag,
              // monospace, scroll-x, copy button. If the agent forgot the
              // fence we auto-wrap the body so plain ASCII still lands in
              // a `<pre>` rather than collapsing into a paragraph.
              <Markdown className="prose-sm max-w-none" density="chat">
                {/```/.test(previewSource)
                  ? previewSource
                  : "```text\n" + previewSource.trim() + "\n```"}
              </Markdown>
            ) : (
              <p className="text-xs italic text-muted-foreground">
                Hover or pick an option to preview.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The one place a row's three possible looks are decided, shared by the
 * option rows and the "Other" row so they cannot drift apart.
 *
 * **Picked is the loudest state, and it looks the same however it was
 * picked** — mouse, digit hotkey or ↑/↓. It was previously the quietest:
 * selection drew a 1px 40%-opacity ring while the keyboard cursor drew a
 * solid grey fill, so the row the user had merely *moved past* shouted
 * louder than the row they had chosen, and a stray browser focus outline
 * added a third box somewhere else again.
 *
 * The cursor and hover keep a neutral hairline border instead — enough to
 * follow ↑/↓ or see what the pointer is over. **A filled row means picked
 * and nothing else**, so a row the mouse merely rests on can never be
 * mistaken for the answer.
 */
function rowStateClass(selected: boolean, active: boolean): string {
  if (selected) return "border-primary bg-muted"
  if (active) return "border-muted-foreground/25"
  return "border-transparent hover:border-muted-foreground/20"
}

interface OptionRowProps {
  option: AskUserQuestionOption
  questionNumber: number
  selected: boolean
  active: boolean
  multiSelect: boolean
  onClick: () => void
  /** Lets the card move DOM focus with the keyboard cursor. */
  rowRef?: (el: HTMLButtonElement | null) => void
}

function OptionRow({
  option,
  questionNumber,
  selected,
  active,
  multiSelect,
  onClick,
  rowRef,
}: OptionRowProps) {
  const recommended = hasRecommendedSuffix(option.label)
  const displayLabel = stripRecommendedSuffix(option.label)
  return (
    <li>
      <button
        ref={rowRef}
        type="button"
        onClick={onClick}
        className={cn(
          "flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
          // The browser's own focus ring is suppressed on purpose: DOM focus
          // is kept on the cursor row (see the focus effect in the card), so
          // the outline could only ever draw a *third* box on a row that the
          // styles below already describe. Three competing outlines is what
          // made "which one did I pick?" unanswerable.
          "outline-none",
          rowStateClass(selected, active),
        )}
        aria-pressed={selected}
      >
        <span className="pt-0.5 text-sm tabular-nums text-muted-foreground">
          {questionNumber}.
        </span>
        {multiSelect ? (
          <span
            className={cn(
              "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors",
              selected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-muted-foreground/40 bg-background",
            )}
            aria-hidden
          >
            {selected ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="h-3.5 w-3.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : null}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            {displayLabel}
            {recommended ? (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                Recommended
              </span>
            ) : null}
            <span
              className="text-muted-foreground/70 hover:text-muted-foreground"
              title={option.description}
              aria-label={option.description}
            >
              <Info className="h-3 w-3" />
            </span>
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {option.description}
          </p>
        </div>
      </button>
    </li>
  )
}

interface OtherRowProps {
  questionNumber: number
  selected: boolean
  active: boolean
  value: string
  onSelect: () => void
  onChange: (text: string) => void
  inputRef: (el: HTMLInputElement | null) => void
}

function OtherRow({
  questionNumber,
  selected,
  active,
  value,
  onSelect,
  onChange,
  inputRef,
}: OtherRowProps) {
  return (
    <li>
      <div
        className={cn(
          "flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors",
          rowStateClass(selected, active),
        )}
      >
        <button
          type="button"
          onClick={onSelect}
          className="pt-0.5 text-sm tabular-nums text-muted-foreground"
          aria-label="Other"
        >
          {questionNumber}.
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Other</p>
          <input
            ref={inputRef}
            type="text"
            value={value}
            placeholder="Type your answer…"
            onFocus={onSelect}
            onChange={(e) => {
              onChange(e.target.value)
              if (!selected) onSelect()
            }}
            className="mt-1 w-full border-b border-border bg-transparent py-1 text-xs text-foreground outline-none focus:border-foreground/40"
          />
        </div>
      </div>
    </li>
  )
}
